import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap } from '@/store/slices/creativeJobs';

import { ROUTE } from '../../../../routes';
import { fetchArtStyle, listArtStyles } from '../../../art-style/art-style-api';
import { isActiveJob, isExtractJobFor, isSampleJobFor } from '../../../art-style/sample-jobs';
import { resolveSummary, unaddressableView, type WebsiteCardView } from '../../card-summary';
import { useBrandSettled } from '../../useBrandSettled';
import { artStyleCopy } from './art-style-copy';
import { deriveArtStyleSummary, primaryArtStyle, type ArtStyleCardInput } from './art-style-summary';

/** What the reads returned, stamped with the tenant they were made for. */
type Reads = { tenantId: number } & Pick<ArtStyleCardInput, 'status' | 'styles' | 'palette'>;

const PENDING: Pick<ArtStyleCardInput, 'status' | 'styles' | 'palette'> = {
  status: 'loading',
  styles: [],
  palette: null,
};

/** The list, then the primary style's palette from its record. A failed record read keeps the name, no swatches. */
const readArtStyles = async (tenantId: number, signal: AbortSignal): Promise<Omit<Reads, 'tenantId'>> => {
  const list = await listArtStyles(tenantId, signal);
  if (!list.ok) return { status: 'failed', styles: [], palette: null };
  const styles = list.value.map((style) => ({
    id: style.id,
    name: style.name,
    active: style.status === 'active',
    hasDefinition: style.has_definition,
    thumbUrl: style.thumb_url,
  }));
  const primary = primaryArtStyle(styles);
  if (!primary || signal.aborted) return { status: 'ready', styles, palette: null };
  const record = await fetchArtStyle(tenantId, primary.id, signal);
  return { status: 'ready', styles, palette: record.ok ? record.value.palette.map((swatch) => swatch.hex) : null };
};

/**
 * The Website Art Style card: reads the brand's style list and, when one is primary, that style's record for its
 * palette. Read only, by design: the Art Style page's own list hook (`useArtStyles`) creates a blank draft for a brand
 * with no styles, so the card never uses it and the page still makes that draft only when the owner opens it. The
 * "Drawing samples" chip comes from the jobs already live in the store. Reads of a superseded brand are aborted and
 * dropped.
 */
export const useArtStyleCard = (): WebsiteCardView => {
  const { t } = useTranslation();
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;

  const [reads, setReads] = React.useState<Reads | null>(null);

  React.useEffect(() => {
    if (tenantId === undefined) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      const next = await readArtStyles(tenantId, signal);
      if (!signal.aborted) setReads({ tenantId, ...next });
    })();
    return () => controller.abort();
  }, [tenantId]);

  const jobsById = useAppSelector(selectCreativeJobsMap);
  const drawing = React.useMemo(
    () =>
      tenantId !== undefined &&
      Object.values(jobsById).some(
        (job) => isActiveJob(job) && (isSampleJobFor(job, tenantId) || isExtractJobFor(job, tenantId)),
      ),
    [jobsById, tenantId],
  );

  if (tenantId === undefined) {
    return unaddressableView(brandSettled, { label: t('Open art style'), to: ROUTE.artStyle() });
  }
  // Until this brand's reads land, the card shows them as loading, never the previous brand's.
  const current = reads?.tenantId === tenantId ? reads : PENDING;
  return resolveSummary(
    deriveArtStyleSummary({ status: current.status, styles: current.styles, palette: current.palette, drawing }),
    (c) => artStyleCopy(t, c),
    () => undefined,
  );
};
